(() => {
  if (customElements.get('bh-promo-slider')) return;

  class BHPromoSlider extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.track = this.querySelector('[data-promo-track]');
      this.slides = [...this.querySelectorAll('[data-promo-slide]')];
      if (!this.track || !this.slides.length) return;

      this.controller = new AbortController();
      this.signal = this.controller.signal;
      this.dots = [...this.querySelectorAll('[data-promo-dot]')];
      this.controls = this.querySelector('[data-promo-controls]');
      this.videoButton = this.querySelector('[data-promo-video-toggle]');
      this.status = this.querySelector('[data-promo-status]');
      this.mobile = matchMedia('(max-width: 749px)');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.interval = Math.max(4000, Number(this.dataset.interval) || 7000);
      this.style.setProperty('--bh-promo-zoom-duration', `${this.interval}ms`);
      this.slides.forEach((slide) => slide.classList.toggle('has-video', !!slide.querySelector('[data-promo-video]')));
      this.autoEnabled = this.dataset.autoplay === 'true' && this.slides.length > 1;
      this.rotationPaused = this.reduced.matches;
      this.videoChoices = new Map();
      this.index = 0;
      this.visible = false;
      this.pointer = null;
      this.scrolling = false;
      this.hovered = false;
      this.focusPaused = false;
      this.editorSelected = false;
      this.resumeAt = 0;
      this.suppressClickUntil = 0;
      this.timer = this.settleTimer = this.frame = 0;
      this.video = null;
      this.videoKey = '';
      if (this.controls) this.controls.hidden = false;

      const on = (target, name, handler, options = {}) =>
        target?.addEventListener(name, handler, { ...options, signal: this.signal });

      on(this, 'click', (event) => {
        if (event.target.closest('a') && Date.now() < this.suppressClickUntil) {
          event.preventDefault();
          event.stopPropagation();
        }
      }, { capture: true });
      this.dots.forEach((dot, index) => on(dot, 'click', () => this.go(index, true)));
      on(this.querySelector('[data-promo-prev]'), 'click', () => this.go(this.index - 1, true));
      on(this.querySelector('[data-promo-next]'), 'click', () => this.go(this.index + 1, true));
      on(this.videoButton, 'click', () => {
        this.interact();
        const play = !this.video || this.video.paused;
        this.videoChoices.set(this.slides[this.index].dataset.blockId, play);
        this.syncVideo();
        this.schedule();
      });

      on(this.track, 'keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.rotationPaused = !this.rotationPaused;
          this.status.textContent = this.rotationPaused ? 'Movimiento pausado' : 'Movimiento reanudado';
          this.schedule();
          return;
        }
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const target = event.key === 'Home' ? 0 : event.key === 'End' ? this.slides.length - 1 : this.index + (event.key === 'ArrowRight' ? 1 : -1);
        this.go(target, true);
      });
      on(this.track, 'dragstart', (event) => event.preventDefault());
      on(this.track, 'pointerdown', (event) => {
        if (!event.isPrimary || event.button !== 0) return;
        this.interact();
        this.suppressClickUntil = 0;
        this.pointer = { id: event.pointerId, type: event.pointerType, x: event.clientX, y: event.clientY, left: this.track.scrollLeft, dragging: false };
      }, { passive: true });
      on(window, 'pointermove', (event) => {
        const point = this.pointer;
        if (!point || point.id !== event.pointerId || point.type !== 'mouse') return;
        const dx = event.clientX - point.x;
        const dy = event.clientY - point.y;
        if (!point.dragging && (Math.abs(dx) < 8 || Math.abs(dx) < Math.abs(dy))) return;
        point.dragging = true;
        this.track.classList.add('is-dragging');
        event.preventDefault();
        this.track.scrollLeft = point.left - dx;
        this.suppressClickUntil = Date.now() + 400;
      });
      const release = (event) => {
        if (!this.pointer || this.pointer.id !== event.pointerId) return;
        const dragged = this.pointer.dragging;
        if (dragged || event.type === 'pointercancel') this.suppressClickUntil = Date.now() + 400;
        this.pointer = null;
        this.track.classList.remove('is-dragging');
        if (dragged) this.go(this.nearest(), true);
        else this.schedule();
      };
      on(window, 'pointerup', release);
      on(window, 'pointercancel', release);
      on(this.track, 'wheel', () => this.interact(), { passive: true });
      on(this.track, 'scroll', () => {
        this.stop();
        this.scrolling = true;
        this.classList.add('is-motion-paused');
        this.video?.pause();
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => this.settle(), 140);
        if (!this.frame) this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.updateDots(this.nearest());
        });
      }, { passive: true });
      on(this.track, 'scrollend', () => this.settle());

      on(this, 'pointerenter', (event) => {
        if (event.pointerType === 'mouse') { this.hovered = true; this.schedule(); }
      });
      on(this, 'pointerleave', (event) => {
        if (event.pointerType === 'mouse') { this.hovered = false; this.schedule(); }
      });
      const checkFocus = () => queueMicrotask(() => {
        if (!this.controller) return;
        this.focusPaused = this.contains(document.activeElement) && document.activeElement.matches(':focus-visible');
        this.schedule();
      });
      on(this, 'focusin', checkFocus);
      on(this, 'focusout', checkFocus);
      on(document, 'visibilitychange', () => { this.syncVideo(); this.schedule(); });
      on(this.mobile, 'change', () => this.syncVideo());
      on(this.reduced, 'change', () => {
        if (this.reduced.matches) { this.rotationPaused = true; this.videoChoices.clear(); }
        this.updateControls();
        this.syncVideo();
        this.schedule();
      });
      on(document, 'shopify:block:select', (event) => {
        const index = this.slides.findIndex((slide) => slide.dataset.blockId === event.detail?.blockId);
        if (index < 0) return;
        this.editorSelected = true;
        this.go(index, true, false);
      });
      on(document, 'shopify:block:deselect', (event) => {
        if (!this.slides.some((slide) => slide.dataset.blockId === event.detail?.blockId)) return;
        this.editorSelected = false;
        this.interact();
        this.schedule();
      });

      this.observer = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting && entry.intersectionRatio >= 0.25;
        this.syncVideo();
        this.schedule();
      }, { threshold: [0, 0.25] });
      // Observar la pieza visible para pausar el video fuera de pantalla.
      this.observer.observe(this.track);
      this.resizeObserver = new ResizeObserver(() => {
        this.style.setProperty('--bh-promo-viewport-width', `${document.documentElement.clientWidth}px`);
        const width = this.track.clientWidth;
        if (!width || width === this.lastWidth) return;
        this.lastWidth = width;
        this.track.scrollTo({ left: this.index * width, behavior: 'instant' });
      });
      this.resizeObserver.observe(this.track);
      this.resizeObserver.observe(document.documentElement);
      this.settle();
    }

    nearest() {
      return Math.max(0, Math.min(this.slides.length - 1, Math.round(this.track.scrollLeft / (this.track.clientWidth || 1))));
    }

    interact() {
      this.resumeAt = Date.now() + Math.max(8000, this.interval);
      this.manualChange = true;
      this.classList.add('is-motion-paused');
      this.stop();
    }

    go(index, manual = false, animate = true) {
      if (manual) this.interact();
      const target = (index + this.slides.length) % this.slides.length;
      this.scrolling = true;
      this.classList.add('is-motion-paused');
      this.video?.pause();
      this.track.scrollTo({ left: target * this.track.clientWidth, behavior: animate && !this.reduced.matches ? 'smooth' : 'instant' });
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => this.settle(), 160);
    }

    settle() {
      if (this.pointer?.dragging) return;
      clearTimeout(this.settleTimer);
      this.scrolling = false;
      const previous = this.index;
      this.index = this.nearest();
      this.slides.forEach((slide, index) => {
        slide.inert = index !== this.index;
        slide.classList.toggle('is-active', index === this.index);
        slide.setAttribute('aria-hidden', String(index !== this.index));
      });
      this.updateControls();
      if (this.manualChange && previous !== this.index && this.status) {
        this.status.textContent = `Promoción ${this.index + 1} de ${this.slides.length}`;
      }
      this.manualChange = false;
      this.syncVideo();
      this.schedule();
    }

    updateDots(index) {
      this.dots.forEach((dot, i) => {
        if (i === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    updateControls() {
      this.updateDots(this.index);
    }

    stop() { clearTimeout(this.timer); this.timer = 0; }

    schedule() {
      this.stop();
      const blocked = this.rotationPaused || !this.visible || document.hidden || this.pointer || this.scrolling || this.hovered || this.focusPaused || this.editorSelected;
      this.classList.toggle('is-motion-paused', !!blocked);
      if (!this.autoEnabled || blocked) return;
      this.timer = setTimeout(() => this.go(this.index + 1), Math.max(this.interval, this.resumeAt - Date.now()));
    }

    syncVideo() {
      const slide = this.slides[this.index];
      const mode = this.mobile.matches ? 'mobile' : 'desktop';
      const template = slide.querySelector(`[data-promo-video="${mode}"]`);
      const key = `${this.index}:${mode}`;
      if (key !== this.videoKey) this.unmountVideo();
      if (this.videoButton) this.videoButton.hidden = !template;
      if (!template) return;
      if (!this.visible || document.hidden || this.scrolling) { this.video?.pause(); return; }
      if (!this.video) {
        this.videoKey = key;
        this.video = template.content.querySelector('video').cloneNode(true);
        const video = this.video;
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.loop = true;
        video.preload = 'metadata';
        video.setAttribute('aria-hidden', 'true');
        video.tabIndex = -1;
        ['play', 'pause', 'error'].forEach((event) => video.addEventListener(event, () => {
          if (video !== this.video) return;
          if (event === 'error') video.classList.remove('is-ready');
          this.updateVideoButton();
        }, { signal: this.signal }));
        video.addEventListener('loadeddata', () => video.classList.add('is-ready'), { once: true, signal: this.signal });
        slide.querySelector('.bh-promo__media').append(video);
      }
      const chosen = this.videoChoices.get(slide.dataset.blockId);
      const shouldPlay = chosen ?? (this.dataset.videoAutoplay === 'true' && !this.reduced.matches);
      if (shouldPlay && this.video.paused) {
        const video = this.video;
        video.play()?.catch(() => { if (video === this.video) this.updateVideoButton(); });
      } else if (!shouldPlay) this.video.pause();
      this.updateVideoButton();
    }

    updateVideoButton() {
      const playing = !!this.video && !this.video.paused && !this.video.error;
      this.videoButton?.classList.toggle('is-playing', playing);
      this.videoButton?.setAttribute('aria-label', playing ? 'Pausar video' : 'Reproducir video');
    }

    unmountVideo() {
      if (this.video) {
        this.video.pause();
        this.video.removeAttribute('src');
        this.video.querySelectorAll('source').forEach((source) => source.removeAttribute('src'));
        this.video.load();
        this.video.remove();
        this.video = null;
      }
      this.videoKey = '';
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.stop();
      clearTimeout(this.settleTimer);
      cancelAnimationFrame(this.frame);
      this.unmountVideo();
    }
  }

  customElements.define('bh-promo-slider', BHPromoSlider);
})();